import { ErrorBoundary } from '@ctw/shared/components/errors/error-boundary';
import { Modal, type ModalProps } from '@ctw/shared/components/modal';
import {
  type TelemetryContext,
  useTelemetry,
} from '@ctw/shared/context/telemetry/telemetry-boundary';
import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import type { SetOptional } from 'type-fest';

export interface OpenModalAction extends Pick<ModalProps, 'content'> {
  telemetryContext?: TelemetryContext;
  onModalEvent?: (eventType: ModalEventType) => void;
}

type ModalEventType = 'open' | 'afterOpen' | 'close' | 'afterClose';

export type ModalEventHandler = (eventType: ModalEventType) => void;

export interface ModalBoundary {
  name: string;
  telemetry: ReturnType<typeof useTelemetry>;
  context?: TelemetryContext;
}

type ModalLayoutMode = 'fit' | 'full';
type ModalLayoutOverflowBehavior = 'visible' | 'scroll' | 'hidden';

export interface ModalLayout {
  mode: ModalLayoutMode;
  overflowBehavior: ModalLayoutOverflowBehavior;
  fullbleed: boolean;
  showCloseButton: boolean;
}

export interface OpenModalArgs {
  telemetry: ReturnType<typeof useTelemetry>;
  telemetryContext?: TelemetryContext;
  action: OpenModalAction;
}

export interface ModalState {
  isModalOpen: boolean;
  openModal: (args: OpenModalArgs) => void;
  closeModal: () => void;
  modalBoundary: ModalBoundary;
  setModalBoundary: (boundary: ModalBoundary) => void;
  setLayout: (layout: ModalLayout) => void;
  onModalEvent: (boundaryName: string, eventHandler: ModalEventHandler) => () => void;
}

const emptyModalAction: OpenModalAction = {
  content: {
    body: null,
  },
} as const;

const defaultModalLayout: ModalLayout = {
  mode: 'fit',
  overflowBehavior: 'scroll',
  fullbleed: false,
  showCloseButton: true,
} as const;

const defaultModalBoundary: SetOptional<ModalBoundary, 'telemetry'> = {
  name: 'Modal',
} as const;

const ModalContext = createContext<ModalState | null>(null);

interface ModalProviderChildrenArgs {
  modal: ReactNode;
}

interface ModalProviderProps {
  children: (args: ModalProviderChildrenArgs) => ReactNode;
}

export const ModalProvider = ({ children }: ModalProviderProps) => {
  const providerTelemetry = useTelemetry();
  const [openModalAction, setOpenModalAction] = useState<OpenModalAction>(emptyModalAction);
  const [modalBoundary, setModalBoundary] = useState<ModalBoundary>({
    ...defaultModalBoundary,
    telemetry: providerTelemetry,
  });
  const [layout, setLayout] = useState<ModalLayout>(defaultModalLayout);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const eventRegistrations: Record<string, Array<ModalEventHandler>> = useMemo(() => ({}), []);

  const openModal: ModalState['openModal'] = useCallback(
    ({ telemetry, action }) => {
      telemetry.trackInteraction('modal_open', {
        ...modalBoundary.context,
        modalName: modalBoundary.name,
        ...action.telemetryContext,
      });
      setIsModalOpen(true);
      setOpenModalAction(action);
    },
    [modalBoundary],
  );

  const closeModal: ModalState['closeModal'] = useCallback(() => {
    handleEvent('close');
    modalBoundary.telemetry.trackInteraction('modal_close', {
      ...modalBoundary.context,
      modalName: modalBoundary.name,
      ...openModalAction.telemetryContext,
    });
    setIsModalOpen(false);
    setTimeout(() => {
      setOpenModalAction(emptyModalAction);
      setModalBoundary({ ...defaultModalBoundary, telemetry: providerTelemetry });
      setLayout(defaultModalLayout);
    }, 300);
  }, [providerTelemetry, modalBoundary, openModalAction]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: No explanation was provided. Please be a good citizen and add one!
  const state: ModalState = useMemo(
    () => ({
      openModal,
      closeModal,
      isModalOpen,
      modalBoundary,
      setModalBoundary,
      setLayout,
      onModalEvent: (boundaryName, eventHandler) => {
        if (!Array.isArray(eventRegistrations[boundaryName])) {
          eventRegistrations[boundaryName] = [];
        }

        eventRegistrations[boundaryName].push(eventHandler);

        return () => {
          eventRegistrations[boundaryName] = eventRegistrations[boundaryName].filter(
            (handler) => handler !== eventHandler,
          );
        };
      },
    }),
    [modalBoundary, closeModal, isModalOpen, openModal],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: No explanation was provided. Please be a good citizen and add one!
  const handleEvent = useCallback(
    (eventType: ModalEventType) => {
      openModalAction.onModalEvent?.(eventType);

      if (Array.isArray(eventRegistrations[modalBoundary.name])) {
        eventRegistrations[modalBoundary.name].forEach((eventHandler) => {
          const invokeEventHandler = async () => eventHandler(eventType);
          void invokeEventHandler();
        });
      }
    },
    [modalBoundary, openModalAction],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: No explanation was provided. Please be a good citizen and add one!
  const modal = useMemo(
    () => (
      <ErrorBoundary boundaryName={modalBoundary.name} initialContext={modalBoundary.context}>
        {() => (
          <Modal
            layout={layout}
            isOpen={isModalOpen}
            onOpen={() => handleEvent('open')}
            onAfterOpen={() => handleEvent('afterOpen')}
            close={closeModal}
            onAfterClose={() => handleEvent('afterClose')}
            {...openModalAction}
          />
        )}
      </ErrorBoundary>
    ),
    [modalBoundary, closeModal, handleEvent, isModalOpen, openModalAction],
  );

  return (
    <ModalContext.Provider value={state}>
      {children({
        modal,
      })}
    </ModalContext.Provider>
  );
};

export interface UseModalOptions {
  boundaryName: string;
  layout?: ModalLayout;
  telemetryContext?: TelemetryContext;
  onModalEvent?: (eventType: ModalEventType) => void;
}

export const useModal = ({
  boundaryName,
  layout,
  telemetryContext,
  onModalEvent,
}: UseModalOptions) => {
  const hookTelemetry = useTelemetry();
  const modalContext = useContext(ModalContext);

  // biome-ignore lint/correctness/useExhaustiveDependencies: No explanation was provided. Please be a good citizen and add one!
  const modal: ModalState | null = useMemo(() => {
    if (!modalContext) {
      return null;
    }

    return {
      ...modalContext,
      openModal: ({ telemetry, telemetryContext, action }: OpenModalArgs) => {
        modalContext.setModalBoundary({
          name: boundaryName,
          telemetry,
          context: {
            ...hookTelemetry.getScopedContextProperties(),
            ...telemetryContext,
            modalName: boundaryName,
            ...action.telemetryContext,
            ...telemetryContext,
          },
        });

        if (layout) {
          modalContext.setLayout(layout);
        }

        modalContext.openModal({
          telemetry,
          action: {
            ...action,
            telemetryContext: {
              ...hookTelemetry.getScopedContextProperties(),
              ...telemetryContext,
              modalName: boundaryName,
              ...action.telemetryContext,
            },
          },
        });
      },
    };
  }, [boundaryName, telemetryContext, modalContext]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: No explanation was provided. Please be a good citizen and add one!
  useLayoutEffect(
    () =>
      modal && onModalEvent
        ? modal.onModalEvent(boundaryName, onModalEvent)
        : () => {
            /* noop */
          },
    [boundaryName, onModalEvent],
  );

  if (!modal) {
    throw new Error('useModal must be used within a ModalProvider');
  }

  return modal;
};
